import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Button,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Collapse,
    CircularProgress,
    Badge,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useParams, useNavigate } from 'react-router-dom';
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ProfitLossModal from '../components/markets/ProfitLossModal';
import axios from 'axios';

// ═══════════════════════════════════════════════════════════════════════════
// API CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL = process.env.REACT_APP_WHOOK_API_URL || 'https://api.whook.ai';

// ═══════════════════════════════════════════════════════════════════════════
// DUMMY DATA (kept as-is)
// ═══════════════════════════════════════════════════════════════════════════

const locations = [
    {
        id: 1,
        country: 'United States',
        state: 'California',
        city: 'Los Angeles',
        teamLeader: 'John Doe',
        employees: ['Alice Smith', 'Bob Johnson'],
        lat: 34.0522,
        lng: -118.2437,
        coverage: 80,
        neighborhoods: ['Downtown', 'Hollywood'],
    },
    {
        id: 2,
        country: 'United States',
        state: 'California',
        city: 'San Francisco',
        teamLeader: 'Jane Doe',
        employees: ['Charlie Brown', 'Dana White'],
        lat: 37.7749,
        lng: -122.4194,
        coverage: 75,
        neighborhoods: ['Mission District', 'SoMa'],
    },
    // Additional locations...
];

const employeesList = [
    'Alice Smith', 'Bob Johnson', 'Charlie Brown', 'Dana White', 'Eve Black',
    'Frank Green', 'Grace Lee', 'Henry Scott', 'Olivia Harris', 'Noah Martin',
    'Sophia Brown', 'Liam Wilson', 'Isabella Clark', 'Lucas King',
    'Emma Thomas', 'Mason Lee', 'Sophia Roberts', 'James Scott',
    'Andrew White', 'Diana Ross'
];

const countryMapConfig = {
    'United States': { center: { lat: 39.8283, lng: -98.5795 }, zoom: 4 },
    Canada: { center: { lat: 56.1304, lng: -106.3468 }, zoom: 4 },
    Australia: { center: { lat: -25.2744, lng: 133.7751 }, zoom: 4 },
    'United Kingdom': { center: { lat: 54.526, lng: -2.035 }, zoom: 5 },
};

// ═══════════════════════════════════════════════════════════════════════════
// STYLED COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

const PageContainer = styled(Box)({
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
});

const Title = styled(Typography)({
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: '2rem',
});

const ContentContainer = styled(Box)({
    flexGrow: 1,
});

const CustomPaper = styled(Paper)({
    padding: '1rem',
    backgroundColor: '#282828',
    color: '#fff',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    position: 'relative',
    minHeight: '250px',
    borderRadius: '8px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.3)',
});

const CoverageBadge = styled(Box)({
    backgroundColor: '#12c099',
    color: '#fff',
    padding: '0.15rem 0.3rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    display: 'inline-block',
    marginBottom: '0.2rem',
});

const OpportunityBadge = styled(Box)(({ count }) => ({
    backgroundColor: count > 0 ? '#9c27b0' : '#444',
    color: '#fff',
    padding: '0.15rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.75rem',
    fontWeight: 'bold',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    marginLeft: '8px',
    cursor: count > 0 ? 'pointer' : 'default',
    '&:hover': {
        backgroundColor: count > 0 ? '#7b1fa2' : '#444',
    },
}));

const ButtonGroup = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
});

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const CountryOverview = () => {
    const { country } = useParams();
    const navigate = useNavigate();
    const decodedCountry = decodeURIComponent(country);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [assigningLocation, setAssigningLocation] = useState(null);
    const [locationData, setLocationData] = useState(locations);
    const [mapConfig, setMapConfig] = useState(countryMapConfig[decodedCountry] || { center: { lat: 20, lng: 0 }, zoom: 2 });
    const [expanded, setExpanded] = useState({});
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedNeighborhood, setSelectedNeighborhood] = useState(null);
    const [employeeListOpen, setEmployeeListOpen] = useState({});

    // NEW: Opportunity counts per city
    const [opportunityCounts, setOpportunityCounts] = useState({});
    const [loadingOpportunities, setLoadingOpportunities] = useState(true);

    useEffect(() => {
        setMapConfig(countryMapConfig[decodedCountry] || { center: { lat: 20, lng: 0 }, zoom: 2 });
    }, [decodedCountry]);

    // NEW: Fetch opportunity counts for each city in this country
    useEffect(() => {
        const fetchOpportunityCounts = async () => {
            setLoadingOpportunities(true);
            const filteredLocations = locations.filter(loc => loc.country === decodedCountry);
            const counts = {};

            try {
                await Promise.all(
                    filteredLocations.map(async (location) => {
                        try {
                            const response = await axios.get(`${API_BASE_URL}/v1/agent/discovery/opportunities`, {
                                params: {
                                    city: location.city,
                                    status: 'pending_review',
                                    limit: 1
                                }
                            });
                            counts[location.city] = response.data.pagination?.total || 0;
                        } catch (err) {
                            counts[location.city] = 0;
                        }
                    })
                );
                setOpportunityCounts(counts);
            } catch (err) {
                console.error('Failed to fetch opportunity counts:', err);
            } finally {
                setLoadingOpportunities(false);
            }
        };

        fetchOpportunityCounts();
    }, [decodedCountry]);

    const handleAssignEmployee = () => {
        if (selectedEmployee && assigningLocation !== null) {
            const newLocations = locationData.map((location) => {
                if (location.id === assigningLocation) {
                    return { ...location, employees: [...location.employees, selectedEmployee] };
                }
                return location;
            });
            setSelectedEmployee('');
            setAssigningLocation(null);
            setLocationData(newLocations);
        }
    };

    const handleRemoveEmployee = (locationId, employeeName) => {
        const newLocations = locationData.map((location) => {
            if (location.id === locationId) {
                return {
                    ...location,
                    employees: location.employees.filter((employee) => employee !== employeeName),
                };
            }
            return location;
        });
        setLocationData(newLocations);
    };

    const handleExpandClick = (index) => {
        setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const handleProfitLossClick = (location, neighborhood = null) => {
        setSelectedLocation(location);
        setSelectedNeighborhood(neighborhood);
        setModalOpen(true);
    };

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedNeighborhood(null);
    };

    const handleEmployeeToggle = (index) => {
        setEmployeeListOpen((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    // NEW: Navigate to city overview with opportunities tab
    const handleOpportunityClick = (city) => {
        navigate(`/cityoverview/${encodeURIComponent(city)}?tab=opportunities`);
    };

    const filteredLocations = locationData.filter(location => location.country === decodedCountry);

    // Calculate total opportunities for this country
    const totalOpportunities = Object.values(opportunityCounts).reduce((sum, count) => sum + count, 0);

    return (
        <PageContainer>
            <ContentContainer>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Title>{decodedCountry} | Markets</Title>
                    {totalOpportunities > 0 && (
                        <Chip
                            icon={<TrendingUpIcon />}
                            label={`${totalOpportunities} Trend Opportunities`}
                            sx={{ backgroundColor: '#9c27b0', color: '#fff' }}
                        />
                    )}
                </Box>

                <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY}>
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '500px', marginBottom: '20px' }}
                        center={mapConfig.center}
                        zoom={mapConfig.zoom}
                    >
                        {filteredLocations.map((location) => (
                            <Marker
                                key={location.id}
                                position={{ lat: location.lat, lng: location.lng }}
                                title={`${location.city}, ${location.state}`}
                            />
                        ))}
                    </GoogleMap>
                </LoadScript>

                <Grid container spacing={2}>
                    {filteredLocations.map((location, index) => (
                        <Grid item xs={12} md={6} key={location.id}>
                            <CustomPaper>
                                <Box sx={{ marginBottom: '0.5rem' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                        <Typography variant="h6">{location.city}, {location.state}</Typography>

                                        {/* NEW: Opportunity count badge */}
                                        {loadingOpportunities ? (
                                            <CircularProgress size={16} sx={{ color: '#12c099', ml: 1 }} />
                                        ) : (
                                            opportunityCounts[location.city] > 0 && (
                                                <OpportunityBadge
                                                    count={opportunityCounts[location.city]}
                                                    onClick={() => handleOpportunityClick(location.city)}
                                                >
                                                    <TrendingUpIcon sx={{ fontSize: 14 }} />
                                                    {opportunityCounts[location.city]} opportunities
                                                </OpportunityBadge>
                                            )
                                        )}
                                    </Box>
                                    <CoverageBadge>{location.coverage}% Coverage</CoverageBadge>
                                    <Typography variant="body2" sx={{ marginTop: '0.2rem' }}>{location.country}</Typography>
                                </Box>

                                <ButtonGroup>
                                    {/* NEW: View Opportunities button */}
                                    {opportunityCounts[location.city] > 0 && (
                                        <Button
                                            onClick={() => handleOpportunityClick(location.city)}
                                            sx={{ color: '#9c27b0', alignSelf: 'flex-start' }}
                                            startIcon={<TrendingUpIcon />}
                                        >
                                            View Opportunities ({opportunityCounts[location.city]})
                                        </Button>
                                    )}

                                    <Button
                                        onClick={() => handleProfitLossClick(location)}
                                        sx={{ color: '#12c099', alignSelf: 'flex-start' }}
                                    >
                                        Profit/Loss
                                    </Button>

                                    <Button
                                        onClick={() => handleExpandClick(index)}
                                        sx={{ color: '#12c099', alignSelf: 'flex-start' }}
                                        endIcon={expanded[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    >
                                        {expanded[index] ? 'Hide Neighborhoods' : 'Show Neighborhoods'}
                                    </Button>

                                    <Collapse in={expanded[index]} timeout="auto" unmountOnExit>
                                        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {location.neighborhoods.map((neighborhood, nIndex) => (
                                                <Chip
                                                    key={nIndex}
                                                    label={neighborhood}
                                                    sx={{ backgroundColor: '#404040', color: '#fff', cursor: 'pointer' }}
                                                    onClick={() => handleProfitLossClick(location, neighborhood)}
                                                />
                                            ))}
                                        </Box>
                                    </Collapse>

                                    <Button
                                        onClick={() => handleEmployeeToggle(index)}
                                        sx={{ color: '#12c099', alignSelf: 'flex-start' }}
                                        endIcon={employeeListOpen[index] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    >
                                        {employeeListOpen[index] ? 'Hide Employees' : 'Assign Employees'}
                                    </Button>

                                    <Collapse in={employeeListOpen[index]} timeout="auto" unmountOnExit>
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2">Team Leader: {location.teamLeader}</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '8px' }}>
                                                {location.employees.map((employee, empIndex) => (
                                                    <Chip
                                                        key={empIndex}
                                                        label={employee}
                                                        onDelete={() => handleRemoveEmployee(location.id, employee)}
                                                        deleteIcon={<DeleteIcon />}
                                                        sx={{ backgroundColor: '#404040', color: '#fff' }}
                                                    />
                                                ))}
                                            </Box>
                                            <FormControl fullWidth variant="outlined" sx={{ marginTop: '1rem', backgroundColor: '#202020' }}>
                                                <InputLabel sx={{ color: '#12c099' }}>Assign Employee</InputLabel>
                                                <Select
                                                    value={selectedEmployee}
                                                    onChange={(e) => setSelectedEmployee(e.target.value)}
                                                    label="Assign Employee"
                                                    sx={{
                                                        color: '#fff',
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: '#12c099',
                                                        },
                                                    }}
                                                >
                                                    {employeesList
                                                        .filter((employee) => !location.employees.includes(employee))
                                                        .map((employee) => (
                                                            <MenuItem key={employee} value={employee}>
                                                                {employee}
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                            </FormControl>
                                            <Button
                                                variant="contained"
                                                sx={{ backgroundColor: '#12c099', color: '#000', marginTop: '10px' }}
                                                onClick={() => {
                                                    setAssigningLocation(location.id);
                                                    handleAssignEmployee();
                                                }}
                                                startIcon={<AddCircleIcon />}
                                                fullWidth
                                            >
                                                Assign Employee
                                            </Button>
                                        </Box>
                                    </Collapse>
                                </ButtonGroup>
                            </CustomPaper>
                        </Grid>
                    ))}
                </Grid>
            </ContentContainer>

            {selectedLocation && (
                <ProfitLossModal
                    open={modalOpen}
                    onClose={handleModalClose}
                    location={{ ...selectedLocation, neighborhood: selectedNeighborhood }}
                />
            )}
        </PageContainer>
    );
};

export default CountryOverview;